import { lazy, Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { useT, vaultName, vaultDesc, itemName } from '@/i18n';
import { VAULTS } from '@/domain/vaults';
import { useGameStore, AUTO_FARM_INTERVALS_MS } from '@/state/gameStore';
import { useDungeonStore, canEnterVault, effectiveWaveCount, calcScaledHpMult } from '@/state/dungeonStore';
import { WORLD_BOSSES } from '@/domain/bosses';
import { useGroupStore } from '@/state/groupStore';

// Farm-Cooldown approximates a Run's wall-clock time. With a 10 s cooldown
// Farm was ~4.8× the loot/min density of Run, making manual play pointless.
// 60 s puts Run + Farm on equal-density footing — Farm just trades the 20 %
// loot/XP tax for not watching combat.
const FARM_COOLDOWN_MS = 60_000;
import { calcPlayerStats } from '@/domain/combat';
import { getEquippedStats } from '@/domain/equipment';
import { getItem } from '@/domain/items';
import { activeStreakCount, streakMultFromCount } from '@/domain/player';
import type { CombatViewStore3DHandle } from '@/game/three/CombatViewStore3D';
// Lazy-load the entire combat scene (R3F + drei + postprocessing + 3D meshes)
// so the initial bundle stays small. Chunk loads when the player first enters
// a vault.
const CombatViewStore3D = lazy(() =>
  import('@/game/three/CombatViewStore3D').then((m) => ({ default: m.CombatViewStore3D })),
);
import { Icon } from '@/ui/components/Icon';
import { ItemIcon } from '@/ui/components/ItemIcon';
import { ItemDetailModal } from '@/ui/components/ItemDetailModal';
import { SET_NAMES } from '@/domain/itemSets';
import type { EquipmentInstance } from '@/domain/types';
import './DungeonScreen.css';

const TIER_COLOR: Record<string, string> = {
  common: '#a4b8c8', uncommon: '#4affb0', rare: '#4aa7ff',
  epic: '#c64aff', legendary: '#ff9a2b',
};
// Module-level stable empty refs so the lootBundle selector returning null
// during combat doesn't cause a fallback-array re-allocation each render.
const EMPTY_LOOT: Array<{ itemId: string; quantity: number }> = [];
const EMPTY_EQUIPMENT_LOOT: EquipmentInstance[] = [];

export function DungeonScreen() {
  const t = useT();
  const player = useGameStore((s) => s.player);
  const applyDungeonLoot = useGameStore((s) => s.applyDungeonLoot);
  const setBossKillAt = useGameStore((s) => s.setBossKillAt);
  // combatSiphonTap + tickHeat removed in P2.A.5 sunset.
  const autoFarmerVaultId = useGameStore((s) => s.player?.autoFarmerVaultId ?? null);
  const setAutoFarmerVault = useGameStore((s) => s.setAutoFarmerVault);
  const lastAutoFarmAt = useGameStore((s) => s.player?.lastAutoFarmAt ?? 0);

  const phase = useDungeonStore((s) => s.phase);
  const waveIndex = useDungeonStore((s) => s.waveIndex);
  const vaultId = useDungeonStore((s) => s.vaultId);
  const playerHp = useDungeonStore((s) => s.playerHp);
  const playerMaxHp = useDungeonStore((s) => s.playerMaxHp);
  const enemyHp = useDungeonStore((s) => s.enemyHp);
  const enemyMaxHp = useDungeonStore((s) => s.enemyMaxHp);
  const specialCharge = useDungeonStore((s) => s.specialCharge);
  // Phase-gated: loot/equipmentLoot can grow large over an Endless run (40+
  // equipment + 100+ resource entries by W24+). Subscribing directly causes
  // DungeonScreen to re-render on every kill — even though the loot UI is
  // ONLY visible at the end of the run. By returning null when phase isn't
  // a result phase, Zustand's Object.is(null, null) check skips every kill-
  // tick re-render. The single phase transition is enough to surface the
  // arrays for the victory screen.
  const lootBundle = useDungeonStore((s) =>
    (s.phase === 'victory' || s.phase === 'defeat')
      ? { loot: s.loot, equipmentLoot: s.equipmentLoot }
      : null,
  );
  const loot = lootBundle?.loot ?? EMPTY_LOOT;
  const equipmentLoot = lootBundle?.equipmentLoot ?? EMPTY_EQUIPMENT_LOOT;
  // Stack resource drops by itemId for the result screen — without this an
  // Endless run shows 200 separate "Scrap ×1" rows instead of one "Scrap ×237".
  // Iteration order = first-drop-wins, preserving roughly the rarity sequence
  // they came in (since lower-rarity resources spawn earlier in DROP_TABLES).
  const aggregatedLoot = useMemo(() => {
    if (loot.length === 0) return EMPTY_LOOT;
    const totals = new Map<string, number>();
    for (const entry of loot) {
      totals.set(entry.itemId, (totals.get(entry.itemId) ?? 0) + entry.quantity);
    }
    return Array.from(totals, ([itemId, quantity]) => ({ itemId, quantity }));
  }, [loot]);
  const earnedXp = useDungeonStore((s) => s.earnedXp);
  const earnedCorruptedData = useDungeonStore((s) => s.earnedCorruptedData);
  const gameSpeed = useDungeonStore((s) => s.gameSpeed);
  const startRun = useDungeonStore((s) => s.startRun);
  const startBossRun = useDungeonStore((s) => s.startBossRun);
  const flee = useDungeonStore((s) => s.flee);
  const claimLoot = useDungeonStore((s) => s.claimLoot);
  const reset = useDungeonStore((s) => s.reset);
  const setGameSpeed = useDungeonStore((s) => s.setGameSpeed);
  const instantClear = useDungeonStore((s) => s.instantClear);

  const totalWaves = useDungeonStore((s) => s.totalWaves);
  const isEndless = useDungeonStore((s) => s.isEndless);
  const startEndlessRun = useDungeonStore((s) => s.startEndlessRun);

  const groupSession = useGroupStore((s) => s.currentSession);
  const createGroup = useGroupStore((s) => s.createSession);
  const joinGroup = useGroupStore((s) => s.joinSession);
  const leaveGroup = useGroupStore((s) => s.leaveSession);
  const markGroupCompleted = useGroupStore((s) => s.markCompleted);
  const subscribeGroup = useGroupStore((s) => s.subscribeRealtime);
  const [joinCode, setJoinCode] = useState('');

  // Subscribe to realtime updates when in a session
  useEffect(() => {
    if (!groupSession) return;
    const unsub = subscribeGroup(groupSession.id);
    return unsub;
  }, [groupSession?.id, subscribeGroup]);

  // Heat-tick removed in P2.A.5 sunset.

  // Mark group completed when dungeon victory is reached
  useEffect(() => {
    if (phase !== 'victory' || !groupSession || !player) return;
    if (groupSession.vaultId !== vaultId) return;
    void markGroupCompleted(player.id);
  }, [phase, vaultId]);

  const [fightTab, setFightTab] = useState<'dungeons' | 'bosses' | 'endless'>('dungeons');
  const [selectedLootItem, setSelectedLootItem] = useState<EquipmentInstance | null>(null);
  const [farmCooldowns, setFarmCooldowns] = useState<Record<string, number>>({});
  const [, setTick] = useState(0); // forces re-render for countdown

  // Tick every second while any farm cooldown or auto-farm is active
  useEffect(() => {
    const now = Date.now();
    const hasActiveCooldown = Object.values(farmCooldowns).some((t) => t > now);
    const hasAutoFarm = autoFarmerVaultId !== null;
    if (!hasActiveCooldown && !hasAutoFarm) return;
    const iv = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(iv);
  }, [farmCooldowns, autoFarmerVaultId]);

  const combatViewRef = useRef<CombatViewStore3DHandle | null>(null);

  const playerStats = player
    ? calcPlayerStats(player, getEquippedStats(player.equipment, getItem), player.boosts)
    : null;

  const handleStartRun = (id: string) => {
    if (!playerStats || !player) return;
    startRun(id, playerStats, player);
  };

  const handleStartBossRun = (bossId: string) => {
    if (!playerStats || !player) return;
    startBossRun(bossId, playerStats, player);
  };

  const handleStartEndlessRun = () => {
    if (!playerStats || !player) return;
    startEndlessRun(playerStats, player);
  };


  const handleSpecial = () => {
    combatViewRef.current?.triggerSpecial();
  };

  const handleCycleSpeed = () => {
    const next: 1 | 2 | 4 = gameSpeed === 1 ? 2 : gameSpeed === 2 ? 4 : 1;
    setGameSpeed(next);
  };

  /** Start a vault run AND immediately resolve it (farming from vault selection). */
  const handleFarmVault = (id: string) => {
    if (!playerStats || !player) return;
    const cooldownEnds = farmCooldowns[id] ?? 0;
    if (cooldownEnds > Date.now()) return;
    setFarmCooldowns((prev) => ({ ...prev, [id]: Date.now() + FARM_COOLDOWN_MS }));
    startRun(id, playerStats, player, 'farm');
    instantClear(playerStats, player);
  };

  /** Seconds until next auto-farm tick for a vault. 0 = ready / not active. */
  const autoFarmCountdown = (id: string): number => {
    if (autoFarmerVaultId !== id) return 0;
    const vault = VAULTS.find((v) => v.id === id);
    const interval = AUTO_FARM_INTERVALS_MS[vault?.tier ?? 1] ?? 300_000;
    return Math.max(0, Math.ceil((lastAutoFarmAt + interval - Date.now()) / 1000));
  };

  const autoFarmIntervalLabel = (tier: number): string => {
    const ms = AUTO_FARM_INTERVALS_MS[tier] ?? 300_000;
    const min = ms / 60_000;
    return `${min} min`;
  };

  const farmCooldownSec = (id: string): number => {
    const ends = farmCooldowns[id] ?? 0;
    return Math.max(0, Math.ceil((ends - Date.now()) / 1000));
  };

  const handleClaimLoot = () => {
    const result = claimLoot();
    applyDungeonLoot(result.loot, result.equipment, result.xp, result.corruptedData, result.vaultTier);
    // Record boss kill timestamp for cooldown
    if (vaultId?.startsWith('world_boss_')) setBossKillAt(vaultId);
  };

  if (!player || !playerStats) return null;

  // Victory / Defeat screen
  if (phase === 'victory' || phase === 'defeat') {
    return (
      <div className="screen dungeon-screen">
        {selectedLootItem && (
          <ItemDetailModal
            instance={selectedLootItem}
            readOnly
            onClose={() => setSelectedLootItem(null)}
            onUpgrade={() => {}}
          />
        )}
        <div className={`dungeon-result dungeon-result--${phase}`}>
          <div className="dungeon-result__title">
            {phase === 'victory' ? t.dungeon.victory : t.dungeon.defeat}
          </div>
          {phase === 'victory' && (
            <div className="dungeon-result__loot">
              {groupSession && groupSession.members.filter((m) => m.completedAt).length >= 2 && (
                <div className="dungeon-result__group-bonus">
                  👥 Gruppen-Bonus: +{Math.round((groupSession.members.filter((m) => m.completedAt).length - 1) * 20)}% Loot
                </div>
              )}
              <div className="dungeon-result__stat">
                <span>{t.dungeon.xpGained}</span>
                <strong>+{earnedXp} XP</strong>
              </div>
              <div className="dungeon-result__stat">
                <span>{t.dungeon.corruptedDataGained}</span>
                <strong>+{earnedCorruptedData} ⌬</strong>
              </div>

              {/* Equipment drops */}
              {equipmentLoot.length > 0 && (
                <div className="dungeon-result__drops">
                  <div className="dungeon-result__drop-title">⚔ Equipment — anklicken für Details</div>
                  {equipmentLoot.map((inst, i) => {
                    const def = getItem(inst.itemId);
                    const setName = def?.setId ? SET_NAMES[def.setId as keyof typeof SET_NAMES] : null;
                    const isExclusive = def?.isExclusiveBossDrop;
                    return (
                      <button
                        key={i}
                        className={`dungeon-result__drop dungeon-result__drop--${def?.rarity ?? 'common'} dungeon-result__drop--clickable`}
                        onClick={() => setSelectedLootItem(inst)}
                      >
                        <ItemIcon itemId={inst.itemId} sizeRem={1.8} />
                        <div className="dungeon-result__drop-info">
                          <span className="dungeon-result__drop-name">
                            {def ? itemName(def.nameKey) : inst.itemId}
                            <span className="dungeon-result__drop-level"> +{inst.upgradeLevel}</span>
                          </span>
                          <div className="dungeon-result__drop-badges">
                            {setName && (
                              <span className="drop-badge drop-badge--set">◈ {setName}</span>
                            )}
                            {isExclusive && (
                              <span className="drop-badge drop-badge--exclusive">★ Boss-Exklusiv</span>
                            )}
                          </div>
                        </div>
                        <span className="dungeon-result__drop-rarity">{def?.rarity}</span>
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Resource drops — stacked by itemId so a 200-kill endless run
                  shows "Scrap ×237" instead of 200 separate rows. */}
              {aggregatedLoot.length > 0 && (
                <div className="dungeon-result__drops">
                  <div className="dungeon-result__drop-title">💠 Ressourcen</div>
                  {aggregatedLoot.map((l) => {
                    const def = getItem(l.itemId);
                    return (
                      <div key={l.itemId} className={`dungeon-result__drop dungeon-result__drop--${def?.rarity ?? 'common'}`}>
                        <ItemIcon itemId={l.itemId} sizeRem={1.8} />
                        <span>{def ? itemName(def.nameKey) : l.itemId}</span>
                        <span>×{l.quantity}</span>
                      </div>
                    );
                  })}
                </div>
              )}

              <button className="btn btn--primary" onClick={handleClaimLoot}>
                {t.dungeon.claimLoot}
              </button>
            </div>
          )}
          {phase === 'defeat' && (
            <div className="dungeon-result__actions">
              <button className="btn btn--primary" onClick={reset}>
                {t.dungeon.backToVaults}
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Active combat
  if (phase === 'combat') {
    const _vault = VAULTS.find((v) => v.id === vaultId); void _vault;
    const playerPct = playerMaxHp > 0 ? (playerHp / playerMaxHp) * 100 : 100;
    const enemyPct = enemyMaxHp > 0 ? (enemyHp / enemyMaxHp) * 100 : 100;
    const specialReady = specialCharge >= 100;
    // Heat / overheated state removed in P2.A.5 sunset.

    return (
      <div className="dungeon-combat dungeon-combat--v3">
        {/* ── Top strip: Wave info left, enemy HP center, exit-icon right ─── */}
        <div className="combat-hud__top">
          <div className="combat-hud__wave-chip">
            <span className="combat-hud__wave-num">{waveIndex + 1}</span>
            <span className="combat-hud__wave-sep">/</span>
            <span className="combat-hud__wave-total">{isEndless ? '∞' : totalWaves}</span>
            <span className="combat-hud__wave-label">{t.dungeon.wave}</span>
          </div>
          <div className="combat-hud__enemy-hp">
            <div className="combat-hud__enemy-name">Feind</div>
            <div className="combat-hud__enemy-bar">
              <div className="combat-hud__enemy-fill" style={{ width: `${enemyPct}%` }} />
            </div>
            <div className="combat-hud__enemy-num">{enemyHp}/{enemyMaxHp}</div>
          </div>
          <button
            className="combat-hud__exit-btn"
            onClick={() => {
              if (confirm('Vault verlassen? Du verlierst den bisher gesammelten Loot und gewinnst keine XP.')) flee();
            }}
            title={t.dungeon.flee}
          >
            <Icon name="door" size={18} />
          </button>
        </div>

        {/* ── 3D scene fills the entire backdrop ─────────────────────────── */}
        <div className="combat-hud__canvas">
          <Suspense fallback={<div className="combat-hud__canvas-loading">Lade Vault…</div>}>
            <CombatViewStore3D ref={combatViewRef} />
          </Suspense>
        </div>

        {/* ── Bottom-left: circular HP gauge (player health) ─────────────── */}
        <div className="combat-hud__hp-orb">
          <svg viewBox="0 0 100 100" className="combat-hud__hp-orb-svg" aria-hidden>
            {/* Track ring */}
            <circle cx="50" cy="50" r="44" className="combat-hud__hp-orb-track" />
            {/* Fill ring — strokeDashoffset shrinks as HP drops */}
            <circle
              cx="50" cy="50" r="44"
              className="combat-hud__hp-orb-fill"
              style={{
                strokeDasharray: 276.46,
                strokeDashoffset: 276.46 * (1 - playerPct / 100),
              }}
            />
          </svg>
          <div className="combat-hud__hp-orb-inner">
            <Icon name="heart" size={20} className="combat-hud__hp-orb-icon" />
            <div className="combat-hud__hp-orb-value">{playerHp}</div>
            <div className="combat-hud__hp-orb-max">/ {playerMaxHp}</div>
          </div>
        </div>

        {/* ── Bottom-right: Special (big circular) only — Siphon removed in P2.A.5. */}
        <div className="combat-hud__abilities">
          <button
            className={`combat-hud__ability combat-hud__ability--special ${specialReady ? 'is-ready' : ''}`}
            onClick={handleSpecial}
            disabled={!specialReady}
            title={specialReady ? t.dungeon.specialReady : t.dungeon.specialCharging}
          >
            {/* charge fill ring (replaces the old special-bar) */}
            <svg viewBox="0 0 100 100" className="combat-hud__ability-ring" aria-hidden>
              <circle cx="50" cy="50" r="44" className="combat-hud__ability-track" />
              <circle
                cx="50" cy="50" r="44"
                className="combat-hud__ability-charge combat-hud__ability-charge--special"
                style={{
                  strokeDasharray: 276.46,
                  strokeDashoffset: 276.46 * (1 - specialCharge / 100),
                }}
              />
            </svg>
            <Icon name="zap" size={32} />
          </button>
        </div>

        {/* Floating speed toggle, bottom-right */}
        <button
          className="dungeon-combat__speed-fab"
          onClick={handleCycleSpeed}
          title="Geschwindigkeit umschalten"
        >
          <Icon name="forward" size={14} style={{ marginRight: '0.25rem', verticalAlign: '-2px' }} />
          {gameSpeed}x
        </button>
      </div>
    );
  }

  // Vault selection — show all 5 with unlock status
  return (
    <div className="screen dungeon-screen">
      {/* ── Internal tab switcher ──────────────────────────────
          Boss + Endless sind hinter Vault-Tier-Gates: Bosses ab Tier 1
          (= player has cleared at least vault_alpha), Endless ab Tier 3.
          Lock-State zeigt das Tab disabled mit Tooltip statt komplett zu
          verstecken — der Spieler sieht das Ziel der Progression. */}
      {(() => {
        const tier = player.highestVaultTier ?? 0;
        const bossesUnlocked = tier >= 1;
        const endlessUnlocked = tier >= 3;
        return (
          <div className="fight-tabs">
            <button
              className={`fight-tab ${fightTab === 'dungeons' ? 'fight-tab--active' : ''}`}
              onClick={() => setFightTab('dungeons')}
            >
              <Icon name="swords" size={16} style={{ marginRight: '0.4rem', verticalAlign: '-3px' }} />
              Dungeons
            </button>
            <button
              className={`fight-tab ${fightTab === 'bosses' ? 'fight-tab--active' : ''} ${!bossesUnlocked ? 'fight-tab--locked' : ''}`}
              onClick={() => bossesUnlocked && setFightTab('bosses')}
              disabled={!bossesUnlocked}
              title={bossesUnlocked ? '' : 'Schließe Vault Alpha ab, um Weltbosse freizuschalten'}
            >
              <Icon name={bossesUnlocked ? 'skull' : 'lock'} size={16} style={{ marginRight: '0.4rem', verticalAlign: '-3px' }} />
              Bosse
            </button>
            <button
              className={`fight-tab ${fightTab === 'endless' ? 'fight-tab--active' : ''} ${!endlessUnlocked ? 'fight-tab--locked' : ''}`}
              onClick={() => endlessUnlocked && setFightTab('endless')}
              disabled={!endlessUnlocked}
              title={endlessUnlocked ? '' : `Schließe Vault Tier 3 ab (aktuell: Tier ${tier})`}
            >
              <Icon name={endlessUnlocked ? 'zap' : 'lock'} size={16} style={{ marginRight: '0.4rem', verticalAlign: '-3px' }} />
              Endless
            </button>
          </div>
        );
      })()}

      {/* Run-Streak Banner — surfaces the engagement-bonus currently
          active. Re-renders whenever player changes (which happens on
          claimLoot via pushStreakEntry). Hidden when no streak yet so
          the HUD stays clean on first runs. */}
      {(() => {
        const count = activeStreakCount(player);
        const mult = streakMultFromCount(count);
        if (mult <= 1.0) return null;
        return (
          <div className="run-streak-banner" role="status" aria-live="polite">
            <span className="run-streak-banner__flame" aria-hidden>🔥</span>
            <span className="run-streak-banner__label">
              Streak · {count} Runs in 30 min
            </span>
            <span className="run-streak-banner__mult">
              ×{mult.toFixed(2)} Loot + XP
            </span>
          </div>
        );
      })()}

      {fightTab === 'dungeons' && <div className="dungeon-vaults">
        {VAULTS.map((vault, idx) => {
          const check = canEnterVault(vault, player);
          const tierColor = TIER_COLOR[vault.dropTier];
          return (
            <div
              key={vault.id}
              className={`vault-card card ${!check.unlocked ? 'vault-card--locked' : ''}`}
              style={{ '--tier-color': tierColor } as React.CSSProperties}
            >
              <div className="vault-card__header">
                <div className="vault-card__title-wrap">
                  <span className="vault-card__tier-no">#{idx + 1}</span>
                  <h2 className="vault-card__name">{vaultName(vault.id)}</h2>
                </div>
                <span className="vault-card__tier" style={{ color: tierColor, borderColor: tierColor }}>
                  {t.dungeon.tier} {idx + 1}
                </span>
              </div>
              <p className="vault-card__desc">{vaultDesc(vault.id)}</p>
              <div className="vault-card__drops">
                <span className="vault-card__drops-label">Beute:</span>
                <span className="vault-card__drops-val" style={{ color: tierColor }}>
                  {vault.dropTier.toUpperCase()}
                </span>
              </div>
              <div className="vault-card__meta">
                <span>{t.dungeon.waves}: {effectiveWaveCount(vault, player.level)}</span>
                <span>Lv {vault.minLevel}+</span>
                {player.level > vault.minLevel && (
                  <span className="vault-card__scale-hint">
                    ⚔ {calcScaledHpMult(vault, player.level).toFixed(1)}×
                  </span>
                )}
              </div>
              {!check.unlocked && (
                <ul className="vault-card__requirements">
                  {check.reasons.map((r, i) => (
                    <li key={i}>⛔ {t.dungeon.requirement[r.kind as keyof typeof t.dungeon.requirement] ?? r.kind}: {r.missing}</li>
                  ))}
                </ul>
              )}
              <div className="vault-card__actions">
                <button
                  className="btn btn--primary vault-card__btn"
                  disabled={!check.unlocked}
                  onClick={() => handleStartRun(vault.id)}
                >
                  {check.unlocked ? t.dungeon.startRun : t.dungeon.locked}
                </button>
                {check.unlocked && (() => {
                  const cd = farmCooldownSec(vault.id);
                  return (
                    <button
                      className="btn btn--ghost vault-card__farm-btn"
                      onClick={() => handleFarmVault(vault.id)}
                      disabled={cd > 0}
                      title={cd > 0 ? `Cooldown: ${cd}s` : 'Sofort farmen — kein Kampf-Bildschirm'}
                    >
                      {cd > 0 ? `⏳ ${cd}s` : '⏭ Farm'}
                    </button>
                  );
                })()}
                {(() => {
                  const canAutoFarm = check.unlocked && (player.highestVaultTier ?? 0) >= vault.tier;
                  const isActive = autoFarmerVaultId === vault.id;
                  const cd = autoFarmCountdown(vault.id);
                  if (!canAutoFarm) return null;
                  return (
                    <button
                      className={`btn vault-card__auto-btn ${isActive ? 'vault-card__auto-btn--active' : 'btn--ghost'}`}
                      onClick={() => setAutoFarmerVault(isActive ? null : vault.id)}
                      title={isActive
                        ? `Auto-Farm aktiv — alle ${autoFarmIntervalLabel(vault.tier)}, nächster Tick in ${cd}s`
                        : `Auto-Farm: passiv alle ${autoFarmIntervalLabel(vault.tier)} farmen (30% XP / 40% Loot)`}
                    >
                      {isActive ? (cd > 0 ? `🤖 ${cd}s` : '🤖 AN') : t.dungeon.autoFarm}
                    </button>
                  );
                })()}
              </div>
              {check.unlocked && (player.highestVaultTier ?? 0) >= vault.tier && (
                <div className="vault-card__auto-info">
                  ⏰ {t.dungeon.autoFarmInterval.replace('{interval}', autoFarmIntervalLabel(vault.tier))} · 30% XP · 40% Loot
                </div>
              )}

              {/* Group Dungeon section */}
              {check.unlocked && (
                <div className="vault-card__group">
                  {groupSession?.vaultId === vault.id ? (
                    <>
                      <div className="group-info">
                        <span className="group-info__code">Gruppe: {groupSession.code}</span>
                        <span className="group-info__count">{groupSession.members.length} Mitglied(er)</span>
                      </div>
                      <div className="group-members">
                        {groupSession.members.map((m) => (
                          <span key={m.playerId} className={`group-member ${m.completedAt ? 'group-member--done' : ''}`}>
                            {m.completedAt ? '✓' : '○'} {m.runnerName}
                          </span>
                        ))}
                      </div>
                      <button className="btn btn--ghost group-btn" onClick={() => player && leaveGroup(player.id)}>
                        Verlassen
                      </button>
                    </>
                  ) : (
                    <div className="group-join-row">
                      <button
                        className="btn btn--ghost group-btn"
                        onClick={() => player && createGroup(vault.id, player.id, player.name)}
                      >
                        + Gruppe
                      </button>
                      <input
                        className="group-code-input"
                        placeholder="Code"
                        maxLength={6}
                        value={joinCode}
                        onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                      />
                      <button
                        className="btn btn--ghost group-btn"
                        disabled={joinCode.length < 6}
                        onClick={() => { if (player) joinGroup(joinCode, player.id, player.name); setJoinCode(''); }}
                      >
                        Beitreten
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>}

      {fightTab === 'bosses' && (
        <div className="dungeon-bosses dungeon-bosses--tab">
          <p className="dungeon-bosses__sub">Exklusive Beute · Cooldown pro Boss steigt mit Tier (30 s → 8 min)</p>
          {WORLD_BOSSES.some((b) => (player.highestVaultTier ?? 0) >= b.unlockAfterTier) ? WORLD_BOSSES.map((boss) => {
            const unlocked = (player.highestVaultTier ?? 0) >= boss.unlockAfterTier;
            if (!unlocked) return null;
            const lastKill = (player.lastBossKillAt ?? {})[boss.id] ?? 0;
            const cooldownMs = boss.cooldownSec * 1000;
            const cooldownRemaining = Math.max(0, Math.ceil((lastKill + cooldownMs - Date.now()) / 1000));
            const onCooldown = cooldownRemaining > 0;
            const tierColor = TIER_COLOR[boss.bonusDropTier];
            const cooldownLabel = onCooldown
              ? cooldownRemaining >= 60
                ? `${Math.floor(cooldownRemaining / 60)}m ${cooldownRemaining % 60}s`
                : `${cooldownRemaining}s`
              : 'Bereit';
            return (
              <div
                key={boss.id}
                className={`boss-card card ${onCooldown ? 'boss-card--cooldown' : ''}`}
                style={{ '--tier-color': tierColor } as React.CSSProperties}
              >
                <div className="boss-card__header">
                  <span className="boss-card__name">
                    {boss.nameKey.replace('boss.', '').toUpperCase()} — Boss {boss.unlockAfterTier}
                  </span>
                  <span className="boss-card__tier" style={{ color: tierColor }}>{boss.bonusDropTier}</span>
                </div>
                <div className="boss-card__meta">
                  {boss.guaranteedDrops} garantierte Items · {boss.exclusiveItemIds.length} exklusive Drops
                </div>
                <div className="boss-card__cooldown">
                  ⏱ {onCooldown ? cooldownLabel : '✓ Bereit'}
                </div>
                <button
                  className={`btn ${onCooldown ? 'btn--ghost' : 'btn--primary'}`}
                  disabled={onCooldown}
                  onClick={() => handleStartBossRun(boss.id)}
                >
                  {onCooldown ? `⏱ ${cooldownLabel}` : '⚔ Angreifen'}
                </button>
              </div>
            );
          }) : (
            <div className="dungeon-bosses__locked">
              <p>Besiege deinen ersten Vault um Weltbosse freizuschalten.</p>
            </div>
          )}
        </div>
      )}

      {fightTab === 'endless' && (
        <div className="dungeon-endless">
          <p className="dungeon-bosses__sub">
            Überlebe so viele Wellen wie möglich. Gegner werden mit jeder Welle stärker und zahlreicher — Drops auch.
          </p>
          <div className="endless-card card" style={{ '--tier-color': '#ff4af0' } as React.CSSProperties}>
            <div className="endless-card__header">
              <span className="endless-card__title">Endless Fight</span>
              <span className="endless-card__best">
                Bestleistung: <strong>Welle {player.bestEndlessWave ?? 0}</strong>
              </span>
            </div>
            <ul className="endless-card__rules">
              <li>Mob-HP +25%/Welle · Mob-ATK +10%/Welle · Anzahl wächst auf max 10</li>
              <li>Alle 10 Wellen: Boss (×3 HP) — Nexus Apex, Omega Herald, …</li>
              <li>Drops skalieren: Common → Uncommon (W3) → Rare (W7) → Epic (W12) → Legendary (W18)</li>
              <li>Flucht oder Tod beendet den Run — Loot komplett sichtbar am Ende, Wave wird als Highscore gespeichert</li>
            </ul>
            <button
              className="btn btn--primary"
              onClick={handleStartEndlessRun}
            >
              <Icon name="zap" size={16} style={{ marginRight: '0.4rem', verticalAlign: '-3px' }} />
              Run starten
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
